import { useNavigate } from "react-router-dom";
import { toastService } from "@/utils/toastService";
import { QueryStatus } from "@reduxjs/toolkit/query";

import { usePatientInformationService } from "@/rtkQuery/features/hooks/usePatientInformationService";
import { useCheckoutService } from "@/rtkQuery/features/hooks/useCheckoutService";

type HandleCheckoutOptions = {
  shouldToggleCart?: boolean;
  toggleCart?: () => void;
  onStart?: () => void;
  onFinish?: () => void;
};

const hasValidPatientInfo = (entitlements?: any): boolean =>
  !!entitlements?.HasPatientInformation &&
  !!entitlements?.PatientInformationValid;

const isCardMissing = (card: any): boolean => !card;

const isCardBroken = (card: any): boolean =>
  !card?.Hash ||
  !card?.LastFour ||
  typeof card?.ExpMonth !== "number" ||
  typeof card?.ExpYear !== "number";

const isCardValid = (card: any, status: string): boolean =>
  status?.toLowerCase() === "fulfilled" &&
  !!card?.Hash &&
  !!card?.LastFour &&
  typeof card?.ExpMonth === "number" &&
  typeof card?.ExpYear === "number";

export const useHandleCheckout = () => {
  const navigate = useNavigate();
  const { patientEntitlements } = usePatientInformationService();
  const { savedCardData, savedCardStatus, refetchSavedCard } =
    useCheckoutService();

  const handleCheckoutClick = async ({
    shouldToggleCart = false,
    toggleCart,
    onStart,
    onFinish,
  }: HandleCheckoutOptions = {}) => {
    onStart?.();

    const finishCheckout = () => {
      if (shouldToggleCart && toggleCart) toggleCart();
      onFinish?.();
    };


    if (!hasValidPatientInfo(patientEntitlements)) {
      finishCheckout();
      navigate("/checkout?step=user-info");
      return;
    }

    let card = savedCardData;
    let resultStatus = savedCardStatus;

    if (!card && typeof refetchSavedCard === "function") {
      try {
        const fetchedCard = await refetchSavedCard().unwrap();
        card = fetchedCard;
        resultStatus = QueryStatus.fulfilled;
      } catch {
        resultStatus = QueryStatus.rejected;
      }
    }

    if (isCardMissing(card) || isCardBroken(card)) {
      finishCheckout();
      if (isCardBroken(card)) {
        toastService.error(
          "Your saved card appears to be incomplete. Please update it."
        );
      } else {
        toastService.info("Please add a payment method.");
      }
      navigate("/checkout?step=payment");
      return;
    }

    if (isCardValid(card, resultStatus)) {
      finishCheckout();
      navigate("/checkout?step=review");
      return;
    }

    finishCheckout();
    navigate("/checkout?step=payment");
    toastService.warn("We couldn't verify your card. Please try again.");
  };

  return { handleCheckoutClick };
};
